/**
 *
 * 场景：结构上完全相同的多张评分表（如耕地/园地/林地定级指标权重打分表），
 * 当其中任意一张提交后，提示是否把填写的数据回填到另外几张表。
 *
 * - 分组配置以 projectId 为 key，值为「分组数组」，每个分组是 tableId 数组。
 * - 跨表因子 id 通常不同，因此按「因子名 + 父层名」的路径映射取值，保证结构相同即可对齐。
 * - 回填采用懒加载：点击「是」时仅把源表值存入 pending（localStorage），
 *   下次打开目标表（loadScoreTable）时回填，用户可核对后再自行提交。
 */

// 关联评分表分组配置（配置驱动，新增分组只需在此追加）
const RELATED_TABLE_GROUPS = {
  // 项目id = 2v8Kd3UeiJ4vj7y4Pe33nN：耕地 / 园地 / 林地
  "2v8Kd3UeiJ4vj7y4Pe33nN": [
    ["2933", "2934", "2935"], // 耕地定级指标权重打分表
    ["2936", "2937", "2938"], // 园地定级指标权重打分表
    ["2942", "2944", "2946"] // 林地定级指标权重打分表
  ]
};

const promptedKey = (projectId, groupId) =>
  `score_copy_prompted_${projectId}_${groupId}`;

const pendingKey = (projectId, groupId) =>
  `score_copy_pending_${projectId}_${groupId}`;

export const groupIdOf = (group) => group.join("_");

/**
 * 根据 projectId + tableId 找到所属分组（tableId 统一转字符串比较）
 */
export const getGroup = (projectId, tableId) => {
  const groups = RELATED_TABLE_GROUPS[String(projectId)];
  if (!groups) return null;
  const tid = String(tableId);
  return groups.find((g) => g.map(String).indexOf(tid) !== -1) || null;
};

/**
 * 取同组中除自身外的其它表 [{ id, title }]
 */
export const getOtherTables = (projectInfo, group, tableId) => {
  const tid = String(tableId);
  const tables = projectInfo.get("tables") || projectInfo.getIn
    ? projectInfo.get("tables")
    : [];
  if (!tables || !tables.size) return [];
  return group
    .map(String)
    .filter((id) => id !== tid)
    .map((id) => {
      const item = tables.find((i) => String(i.get("id")) === id);
      return {
        id,
        title: item ? item.get("title") : id
      };
    });
};

/**
 * 判断某张表是否已填写过：
 *  - 已评分（status 为真，即已提交）才算填写过；
 *  - 或本地自动保存中确实存在“已输入的数值”。
 * 注意：仅仅“打开过表”（loadScoreTable 会无条件 autoSave）不算填写过，
 * 否则点开另外两张表就会把它们的状态误判为已填写，从而压制复制提示。
 */
export const isTableFilled = (tableId, projectInfo) => {
  const tid = String(tableId);

  // 1) 已评分（已提交）
  const tables = projectInfo.get("tables");
  if (tables && tables.size) {
    const item = tables.find((i) => String(i.get("id")) === tid);
    if (item && item.get("status")) return true;
  }

  // 2) 本地自动保存中确实存在已填写的数值（空值/默认值不算）
  const raw = localStorage.getItem(tid);
  if (raw) {
    try {
      const saved = JSON.parse(raw);
      const t = saved && saved.currentTableInfo;
      if (t) {
        const lists = [t.basic, t.normal, t.sub];
        for (const list of lists) {
          if (Array.isArray(list)) {
            for (const f of list) {
              if (f && f.value != null && f.value !== "") return true;
            }
          }
        }
      }
    } catch (e) {
      // 解析失败时当作未填写
    }
  }

  return false;
};

/**
 * 是否已对该分组提示过（用于「只有三张表都没有填写过才弹出」的去重）
 */
export const isPrompted = (projectId, groupId) =>
  !!localStorage.getItem(promptedKey(projectId, groupId));

export const markPrompted = (projectId, groupId) =>
  localStorage.setItem(promptedKey(projectId, groupId), "1");

/**
 * 读取 pending（待回填数据）
 */
export const getPending = (projectId, groupId) => {
  const raw = localStorage.getItem(pendingKey(projectId, groupId));
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

export const setPending = (projectId, groupId, pending) =>
  localStorage.setItem(pendingKey(projectId, groupId), JSON.stringify(pending));

/**
 * 自动保存缓存（localStorage[tableId]）的轮次记录：
 * 提交/自动保存时写入 { rounds: 当前轮次, currentTableInfo: {...} }，
 * 用于区分"旧轮次数据"与"当前轮草稿"：
 * - 缓存轮次 < 页面轮次 → 旧轮次数据（如第二轮进入时缓存的是第一轮数据），
 *   不弹"是否加载自动保存内容"，且作为基准数据直接回填显示；
 * - 缓存轮次 == 页面轮次 → 当前轮草稿/提交数据，正常弹窗询问。
 */

/**
 * 读取某表本地缓存的轮次（无缓存/解析失败返回 null）
 */
export const getCacheRounds = (tableId) => {
  const raw = localStorage.getItem(String(tableId));
  if (!raw) return null;
  try {
    const saved = JSON.parse(raw);
    return saved && saved.rounds != null ? saved.rounds : null;
  } catch (e) {
    return null;
  }
};

/**
 * 判断某表的本地缓存是否为"当前轮次"的数据（即当前轮草稿/提交数据）：
 * - 缓存轮次与当前轮次一致 → true；
 * - 旧格式缓存（无 rounds 字段）：第一轮（currentRounds===1）视为当前轮草稿，
 *   第二轮起视为旧数据（避免第二轮误弹第一轮的缓存）；
 * - 无缓存 → false。
 */
export const isCurrentRoundDraft = (tableId, currentRounds) => {
  const r = getCacheRounds(tableId);
  if (r == null) {
    return Number(currentRounds) === 1 && !!localStorage.getItem(String(tableId));
  }
  return r === Number(currentRounds);
};

/**
 * 合并自动保存数据：只把 saved 里的 value 回填到 current 的对应因子，
 * 其它字段（name/status/suggest_score/mean/minimum/maximum 等）保留 current 的当前值
 * （建议分值是后端实时字段，不能从本地缓存恢复旧值）。
 * 三层（basic/normal/sub）按因子 id 匹配。current 缺失时返回 saved。
 */
export const mergeValues = (current, saved) => {
  if (!current) return saved;
  if (!saved) return current;
  let result = current;
  ["basic", "normal", "sub"].forEach((level) => {
    const curList = current.get(level) || [];
    const savList = saved.get(level) || [];
    const nextList = curList.map((f) => {
      const savedF = savList.find((s) => s.get("id") === f.get("id"));
      if (!savedF) return f;
      // 只回填 value（用户填写的数据），其它字段保持当前表的值
      return f.set("value", savedF.get("value"));
    });
    result = result.set(level, nextList);
  });
  return result;
};

/**
 * 从源表构建「因子路径 -> 值」映射
 * 路径规则（结构相同即可对齐）：
 *   basic:  b:{name}
 *   normal: n:{父basic名}/{name}
 *   sub:    s:{父basic名}/{父normal名}/{name}
 */
export const buildValueMap = (tableInfo) => {
  const basicList = tableInfo.get("basic") || [];
  const normalList = tableInfo.get("normal") || [];
  const subList = tableInfo.get("sub") || [];

  const basicNameById = {};
  basicList.forEach((f) => {
    basicNameById[f.get("id")] = f.get("name");
  });
  const normalById = {};
  normalList.forEach((f) => {
    normalById[f.get("id")] = f;
  });

  const map = {};
  basicList.forEach((f) => {
    if (f.get("status", 1) !== 0 && f.get("value") != null) {
      map[`b:${f.get("name")}`] = f.get("value");
    }
  });
  normalList.forEach((f) => {
    if (f.get("status", 1) !== 0 && f.get("value") != null) {
      const pName = basicNameById[f.get("parent_id")];
      map[`n:${pName}/${f.get("name")}`] = f.get("value");
    }
  });
  subList.forEach((f) => {
    if (f.get("status", 1) !== 0 && f.get("value") != null) {
      const pNormal = normalById[f.get("parent_id")];
      const pBasicName = basicNameById[pNormal && pNormal.get("parent_id")];
      map[`s:${pBasicName}/${pNormal && pNormal.get("name")}/${f.get("name")}`] =
        f.get("value");
    }
  });
  return map;
};

/**
 * 判断目标因子的取值是否在其允许区间 [minimum, maximum] 内
 * （minimum/maximum 为 null 表示该侧不限）
 */
const valueInRange = (f, v) => {
  const min = f.get("minimum");
  const max = f.get("maximum");
  if (min != null && v < min) return false;
  if (max != null && v > max) return false;
  return true;
};

/**
 * 把源表值映射回填到目标表（仅回填 status!==0 的因子），
 * 源表值不在目标因子区间内时不回填（保持为空），
 * 返回新的 immutable 表对象。
 */
export const applyValueMap = (targetTable, valueMap) => {
  if (!valueMap) return targetTable;

  const basicList = targetTable.get("basic") || [];
  const normalList = targetTable.get("normal") || [];

  const basicNameById = {};
  basicList.forEach((f) => {
    basicNameById[f.get("id")] = f.get("name");
  });
  const normalById = {};
  normalList.forEach((f) => {
    normalById[f.get("id")] = f;
  });

  let result = targetTable;

  result = result.update("basic", (list) =>
    list.map((f) => {
      const v = valueMap[`b:${f.get("name")}`];
      return v != null && valueInRange(f, v) && f.get("status", 1) !== 0
        ? f.set("value", v)
        : f;
    })
  );
  result = result.update("normal", (list) =>
    list.map((f) => {
      const pName = basicNameById[f.get("parent_id")];
      const v = valueMap[`n:${pName}/${f.get("name")}`];
      return v != null && valueInRange(f, v) && f.get("status", 1) !== 0
        ? f.set("value", v)
        : f;
    })
  );
  result = result.update("sub", (list) =>
    list.map((f) => {
      const pNormal = normalById[f.get("parent_id")];
      const pBasicName = basicNameById[pNormal && pNormal.get("parent_id")];
      const v =
        valueMap[`s:${pBasicName}/${pNormal && pNormal.get("name")}/${f.get("name")}`];
      return v != null && valueInRange(f, v) && f.get("status", 1) !== 0
        ? f.set("value", v)
        : f;
    })
  );

  return result;
};
